import React, { useState, useEffect } from 'react';
import { Product, ProductFormData } from '../types';
import { getImageUrl } from '../utils/imageUtils';
import './ProductForm.css';

interface ProductFormProps {
  product: Product | null;
  onSubmit: (formData: ProductFormData) => void;
  onCancel: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({ product, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    category: 'Stuff',
    price: 0,
    length: 0,
    height: 0,
    depth: 0,
    material: 'Wood',
    img_path: '',
    glb_path: '',
    usd_path: '',
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [glbPreview, setGlbPreview] = useState<string | null>(null);
  const [usdzPreview, setUsdzPreview] = useState<string | null>(null);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        category: product.category,
        price: product.price,
        length: product.length,
        height: product.height,
        depth: product.depth,
        material: product.material,
        img_path: product.img_path,
        glb_path: product.glb_path,
        usd_path: product.usd_path,
      });
      setImagePreview(product.img_path ? getImageUrl(product.img_path) : null);
      setGlbPreview(product.glb_path ? getImageUrl(product.glb_path) : null);
      setUsdzPreview(product.usd_path ? getImageUrl(product.usd_path) : null);
    }
  }, [product]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value // Allow typing anything, validation happens on submit
    }));
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>, urlType: 'img_path' | 'glb_path' | 'usd_path') => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [urlType]: value
    }));

    // Update preview for image
    if (urlType === 'img_path') {
      setImagePreview(value ? getImageUrl(value) : null);
    } else if (urlType === 'glb_path') {
      setGlbPreview(value ? getImageUrl(value) : null);
    } else if (urlType === 'usd_path') {
      setUsdzPreview(value ? getImageUrl(value) : null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || formData.name.trim() === '') {
      alert('Product name is required');
      return;
    }
    
    // Parse and validate numeric fields
    const price = parseFloat(formData.price.toString());
    const length = parseFloat(formData.length.toString());
    const height = parseFloat(formData.height.toString());
    const depth = parseFloat(formData.depth.toString());
    
    // Check if values are valid numbers
    if (isNaN(price)) {
      alert('Price must be a valid number');
      return;
    }
    
    if (isNaN(length)) {
      alert('Length must be a valid number');
      return;
    }
    
    if (isNaN(height)) {
      alert('Height must be a valid number');
      return;
    }
    
    if (isNaN(depth)) {
      alert('Depth must be a valid number');
      return;
    }
    
    // Check for negative values
    if (price < 0) {
      alert('Price cannot be negative');
      return;
    }
    
    if (length < 0) {
      alert('Length cannot be negative');
      return;
    }
    
    if (height < 0) {
      alert('Height cannot be negative');
      return;
    }
    
    if (depth < 0) {
      alert('Depth cannot be negative');
      return;
    }
    
    // Round to 2 decimal places and submit
    const roundedFormData = {
      ...formData,
      price: Math.round(price * 100) / 100,
      length: Math.round(length * 100) / 100,
      height: Math.round(height * 100) / 100,
      depth: Math.round(depth * 100) / 100,
    };
    
    onSubmit(roundedFormData);
  };

  return (
    <div className="modal-overlay">
      <div className="product-form-modal">
        <div className="modal-header">
          <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
          <button onClick={onCancel} className="close-button">×</button>
        </div>

        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-grid">
            <div className="form-section">
              <h3>Basic Information</h3>
              
              <div className="form-group">
                <label htmlFor="name">Product Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input
                  type="text"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="Enter category (e.g., Furniture, Decor, Lighting)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="material">Material</label>
                <input
                  type="text"
                  id="material"
                  name="material"
                  value={formData.material}
                  onChange={handleInputChange}
                  placeholder="Enter material (e.g., Wood, Metal, Glass, Plastic)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="price">Price (€) *</label>
                <input
                  type="text"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Dimensions (cm)</h3>
              
              <div className="form-group">
                <label htmlFor="length">Length *</label>
                <input
                  type="text"
                  id="length"
                  name="length"
                  value={formData.length}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="height">Height *</label>
                <input
                  type="text"
                  id="height"
                  name="height"
                  value={formData.height}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="depth">Depth *</label>
                <input
                  type="text"
                  id="depth"
                  name="depth"
                  value={formData.depth}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Media URLs</h3>
              
              <div className="form-group">
                <label htmlFor="img_path">Product Image URL</label>
                <input
                  type="url"
                  id="img_path"
                  name="img_path"
                  value={formData.img_path || ''}
                  onChange={(e) => handleUrlChange(e, 'img_path')}
                  placeholder="/uploads/product-image.jpg"
                  className="form-input"
                />
                {imagePreview && (
                  <div className="url-preview">
                    <img src={imagePreview} alt="Preview" className="preview-image" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="glb_path">3D Model URL (.glb)</label>
                <input
                  type="url"
                  id="glb_path"
                  name="glb_path"
                  value={formData.glb_path || ''}
                  onChange={(e) => handleUrlChange(e, 'glb_path')}
                  placeholder="/uploads/product-model.glb"
                  className="form-input"
                />
                {glbPreview && (
                  <div className="url-preview">
                    <span className="url-name">3D Model: {glbPreview.split('/').pop()}</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="usd_path">AR Model URL (.usdz)</label>
                <input
                  type="url"
                  id="usd_path"
                  name="usd_path"
                  value={formData.usd_path || ''}
                  onChange={(e) => handleUrlChange(e, 'usd_path')}
                  placeholder="/uploads/product-model.usdz"
                  className="form-input"
                />
                {usdzPreview && (
                  <div className="url-preview">
                    <span className="url-name">AR Model: {usdzPreview.split('/').pop()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="submit-button">
              {product ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
